import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, User, Phone, Envelope, Buildings, MapPin, CurrencyInr, Truck,
  Package, Clock, PaperclipHorizontal, PencilSimple, Download, DownloadSimple,
  Receipt, ChatCircleDots, Kanban,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePermissions } from "@/lib/permissions";
import AttachmentsButton from "@/components/AttachmentsButton";

const inr = (v) => "₹" + (v ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const dt  = (s) => s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";

const statusTone = (s) => ({
  pending:       "bg-secondary text-muted-foreground",
  confirmed:     "bg-primary/10 text-primary",
  in_process:    "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  dispatched:    "bg-blue-500/10 text-blue-700 dark:text-blue-300",
  delivered:     "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  cancelled:     "bg-destructive/10 text-destructive line-through",
}[s] || "bg-secondary text-muted-foreground");

const paymentTone = (s) => ({
  unpaid:  "bg-destructive/10 text-destructive",
  advance: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  partial: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
  paid:    "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
}[s] || "bg-secondary");

function Section({ icon: Icon, title, right, children, testid }) {
  return (
    <Card className="p-5 space-y-4" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={14} weight="duotone" className="text-muted-foreground" />
        <div className="overline text-muted-foreground text-[10px]">{title}</div>
        <div className="flex-1" />
        {right}
      </div>
      {children}
    </Card>
  );
}

function KV({ label, value, testid, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={"text-sm mt-0.5 " + (mono ? "font-mono" : "")} data-testid={testid}>
        {value ?? <span className="text-muted-foreground italic">—</span>}
      </div>
    </div>
  );
}

export default function OrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, canEdit } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get(`/orders/${id}/details`);
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load order");
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const generateInvoice = async () => {
    if (!window.confirm(`Generate an invoice for order ${data.order.number}?`)) return;
    try {
      const r = await api.post("/invoices/from-order", { order_id: id, due_days: 15 });
      toast.success(`Invoice ${r.data.number} created`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const createShipment = async () => {
    if (!window.confirm(`Create a Delhivery shipment for ${data.order.number}?`)) return;
    try {
      const r = await api.post("/integrations/delhivery/create-shipment", { order_id: id });
      toast.success(`Shipment created · Waybill ${r.data.waybill || "—"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Shipment failed"); }
  };

  if (loading) {
    return <div className="p-10 text-muted-foreground text-sm" data-testid="order-details-loading">Loading order…</div>;
  }
  if (err) {
    return (
      <div className="p-10 space-y-3" data-testid="order-details-error">
        <Button variant="outline" size="sm" onClick={() => navigate("/orders")}><ArrowLeft size={12} className="mr-1.5" />Back to Orders</Button>
        <Card className="p-8 text-center">
          <div className="font-heading font-bold text-lg text-destructive">Cannot open this order</div>
          <div className="text-sm text-muted-foreground mt-2">{err}</div>
        </Card>
      </div>
    );
  }
  const { order: o, customer: c, payments, timeline, attachments, payment_summary: ps } = data;
  const canEditThis = canEdit("orders.edit", o);

  const items = o.items || [];
  const subtotal = items.reduce((s, it) => s + (it.qty || 0) * (it.price || 0), 0);
  const gstAmt   = items.reduce((s, it) => s + (it.qty || 0) * (it.price || 0) * ((it.gst || 0) / 100), 0);

  return (
    <div className="space-y-6" data-testid="order-details-page">
      {/* Hero */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <button onClick={() => navigate("/orders")}
            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1.5"
            data-testid="order-back-link">
            <ArrowLeft size={12} />Back to Orders
          </button>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight leading-none">Order</h1>
            <span className="font-mono text-lg text-muted-foreground" data-testid="order-number">{o.number}</span>
            <span className={"text-xs uppercase tracking-wider px-2 py-1 rounded-full " + statusTone(o.order_status)} data-testid="order-status-pill">{o.order_status}</span>
            <span className={"text-xs uppercase tracking-wider px-2 py-1 rounded-full " + paymentTone(o.payment_status)} data-testid="order-payment-pill">{o.payment_status}</span>
          </div>
          <div className="text-xs text-muted-foreground">Created {dt(o.created_at)} · by {o.created_by_name || "—"}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEditThis && (
            <Button size="sm" variant="outline" onClick={() => navigate("/orders?edit=" + o.id)} data-testid="order-edit-shortcut">
              <PencilSimple size={13} className="mr-1.5" />Edit
            </Button>
          )}
          {can("invoices.create") && o.order_status !== "cancelled" && (
            <Button size="sm" variant="outline" onClick={generateInvoice} data-testid="order-generate-invoice">
              <Receipt size={13} className="mr-1.5" />Generate Invoice
            </Button>
          )}
          {canEditThis && !o.tracking_number && (
            <Button size="sm" variant="outline" onClick={createShipment} data-testid="order-create-shipment">
              <Truck size={13} className="mr-1.5" />Create Shipment
            </Button>
          )}
          <AttachmentsButton entityType="order" entityId={o.id} label={`Files — ${o.number}`} testId="order-attachments-btn" />
        </div>
      </div>

      {/* Payment summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4" data-testid="ps-total">
          <div className="overline text-muted-foreground text-[10px]">Order Total</div>
          <div className="font-heading text-xl font-bold mt-1">{inr(ps.total)}</div>
        </Card>
        <Card className="p-4" data-testid="ps-paid">
          <div className="overline text-muted-foreground text-[10px]">Received</div>
          <div className="font-heading text-xl font-bold mt-1 text-emerald-700 dark:text-emerald-300">{inr(ps.paid)}</div>
        </Card>
        <Card className="p-4" data-testid="ps-refunded">
          <div className="overline text-muted-foreground text-[10px]">Refunded</div>
          <div className="font-heading text-xl font-bold mt-1">{inr(ps.refunded)}</div>
        </Card>
        <Card className={"p-4 " + (ps.balance_due > 0 ? "border-destructive/40" : "")} data-testid="ps-balance">
          <div className="overline text-muted-foreground text-[10px]">Balance Due</div>
          <div className={"font-heading text-xl font-bold mt-1 " + (ps.balance_due > 0 ? "text-destructive" : "text-emerald-700 dark:text-emerald-300")}>{inr(ps.balance_due)}</div>
        </Card>
      </div>

      <div className="grid md:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6 min-w-0">
          {/* Products */}
          <Section icon={Package} title="Products" testid="section-products">
            <div className="overflow-x-auto -mx-5">
              <table className="w-full text-sm">
                <thead className="bg-secondary/40 text-[10px] uppercase text-muted-foreground tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-2">Item</th>
                    <th className="text-left px-3 py-2">HSN</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-left px-3 py-2">Unit</th>
                    <th className="text-right px-3 py-2">Price</th>
                    <th className="text-right px-3 py-2">GST%</th>
                    <th className="text-right px-5 py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const amt = (it.qty || 0) * (it.price || 0) * (1 + (it.gst || 0) / 100);
                    return (
                      <tr key={i} className="border-t border-border/40" data-testid={`od-item-${i}`}>
                        <td className="px-5 py-2 font-medium">{it.name}</td>
                        <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{it.hsn || "—"}</td>
                        <td className="px-3 py-2 text-right">{it.qty}</td>
                        <td className="px-3 py-2 text-xs">{it.unit || "PCS"}</td>
                        <td className="px-3 py-2 text-right">{inr(it.price)}</td>
                        <td className="px-3 py-2 text-right">{it.gst}%</td>
                        <td className="px-5 py-2 text-right font-medium">{inr(amt)}</td>
                      </tr>
                    );
                  })}
                  {!items.length && <tr><td colSpan={7} className="px-5 py-6 text-center text-muted-foreground">No items on this order.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end pt-3 border-t border-border/40 -mx-5 px-5">
              <div className="w-64 text-sm space-y-1">
                <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>GST</span><span>{inr(gstAmt)}</span></div>
                <div className="flex justify-between font-heading font-bold text-lg pt-1 border-t border-border/40" data-testid="od-total">
                  <span>Total</span><span>{inr(o.total)}</span>
                </div>
              </div>
            </div>
          </Section>

          {/* Payments */}
          <Section icon={CurrencyInr} title="Payments" testid="section-payments"
            right={<span className="text-[10px] text-muted-foreground">{payments.length} record{payments.length === 1 ? "" : "s"}</span>}>
            {payments.length ? (
              <div className="space-y-2">
                {payments.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 p-3 rounded-md border border-border/60" data-testid={`od-payment-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium capitalize">{p.type} · {p.method}</div>
                      <div className="text-[11px] text-muted-foreground">{dt(p.created_at)}{p.reference ? ` · ${p.reference}` : ""}</div>
                      {p.notes && <div className="text-xs mt-1 italic text-muted-foreground">"{p.notes}"</div>}
                    </div>
                    <div className={"font-mono font-semibold " + (p.type === "refund" ? "text-destructive" : "text-emerald-700 dark:text-emerald-300")}>
                      {p.type === "refund" ? "-" : "+"}{inr(p.amount)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground italic">No payments recorded for this order yet.</div>
            )}
          </Section>

          {/* Remarks / Notes */}
          <Section icon={ChatCircleDots} title="Remarks & Notes" testid="section-notes">
            {o.notes ? (
              <div className="text-sm whitespace-pre-wrap" data-testid="od-notes">{o.notes}</div>
            ) : (
              <div className="text-xs text-muted-foreground italic">No remarks on this order.</div>
            )}
          </Section>

          {/* Attachments */}
          <Section icon={PaperclipHorizontal} title="Attachments" testid="section-attachments"
            right={<AttachmentsButton entityType="order" entityId={o.id} label={`Manage — ${o.number}`} testId="od-attachments-manage" />}>
            {attachments.length ? (
              <div className="grid sm:grid-cols-2 gap-2">
                {attachments.map((a) => (
                  <a key={a.id} href={api.defaults.baseURL + "/files/" + a.file_id}
                     target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-3 p-3 rounded-md border border-border/60 hover:bg-secondary/40"
                     data-testid={`od-attachment-${a.id}`}>
                    <PaperclipHorizontal size={14} className="text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{a.filename}</div>
                      <div className="text-[10px] text-muted-foreground">{dt(a.created_at)}</div>
                    </div>
                    <DownloadSimple size={13} className="text-muted-foreground" />
                  </a>
                ))}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground italic">No files attached — click Manage to upload.</div>
            )}
          </Section>

          {/* Activity history */}
          <Section icon={Clock} title="Activity history" testid="section-timeline">
            {timeline.length ? (
              <ol className="relative pl-6 space-y-4 before:absolute before:left-2 before:top-1 before:bottom-1 before:w-px before:bg-border">
                {timeline.map((t) => (
                  <li key={t.id} className="relative" data-testid={`od-timeline-${t.id}`}>
                    <span className="absolute -left-4 top-1.5 w-2 h-2 rounded-full bg-primary" />
                    <div className="text-sm font-medium">{t.title}</div>
                    {t.description && <div className="text-xs text-muted-foreground mt-0.5">{t.description}</div>}
                    <div className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider">{t.type} · {dt(t.created_at)}</div>
                  </li>
                ))}
              </ol>
            ) : (
              <div className="text-xs text-muted-foreground italic">No activity captured for this order yet.</div>
            )}
          </Section>
        </div>

        {/* RIGHT column — Customer, meta, courier */}
        <div className="space-y-6">
          <Section icon={User} title="Customer" testid="section-customer"
            right={c && can("customers.view") ? (
              <Link to={`/customers/${c.id}`} className="text-[11px] text-primary hover:underline" data-testid="od-customer-link">View</Link>
            ) : null}>
            {c ? (
              <div className="space-y-2">
                <div className="font-heading font-bold text-base" data-testid="od-customer-name">{c.customer_name}</div>
                {c.company_name && <div className="text-xs text-muted-foreground">{c.company_name}</div>}
                <div className="space-y-1.5 pt-2">
                  {c.mobile && <div className="flex items-center gap-2 text-xs"><Phone size={11} className="text-muted-foreground" /><span data-testid="od-customer-mobile">{c.mobile}</span></div>}
                  {c.email && <div className="flex items-center gap-2 text-xs"><Envelope size={11} className="text-muted-foreground" /><span data-testid="od-customer-email">{c.email}</span></div>}
                  {c.gst && <div className="flex items-center gap-2 text-xs"><Buildings size={11} className="text-muted-foreground" /><span className="font-mono" data-testid="od-customer-gst">{c.gst}</span></div>}
                </div>
                {(c.address || c.city) && (
                  <div className="pt-3 border-t border-border/40 space-y-1">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Billing</div>
                    <div className="text-xs" data-testid="od-customer-billing">
                      {c.address}
                      {(c.city || c.state) && <div className="text-muted-foreground">{[c.city, c.state, c.pincode].filter(Boolean).join(", ")}</div>}
                    </div>
                  </div>
                )}
                {c.shipping_address && (
                  <div className="pt-3 border-t border-border/40 space-y-1">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Shipping</div>
                    <div className="text-xs" data-testid="od-customer-shipping">{c.shipping_address}</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground italic">Customer record not accessible.</div>
            )}
          </Section>

          <Section icon={Truck} title="Fulfillment" testid="section-fulfillment">
            <div className="space-y-3">
              <KV label="Order status"      value={o.order_status}                     testid="od-order-status" />
              <KV label="Dispatch status"   value={o.dispatch_status}                  testid="od-dispatch-status" />
              <KV label="Transport"         value={o.transport || o.courier}           testid="od-transport" />
              <KV label="Tracking number"   value={o.tracking_number}                  testid="od-tracking" mono />
            </div>
          </Section>

          <Section icon={Kanban} title="Assignment & Source" testid="section-meta">
            <div className="space-y-3">
              <KV label="Assigned to"  value={o.assigned_to_name || "Unassigned"}  testid="od-assigned" />
              <KV label="Order source" value={o.source ? o.source.replace(/_/g, " ") : null} testid="od-source" />
              <KV label="Created by"   value={o.created_by_name}                   testid="od-created-by" />
              <KV label="Payment status" value={o.payment_status}                  testid="od-payment-status" />
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
